import { ExternalLink } from '@oriola-corporation/kronan-icons';
import React, { AnchorHTMLAttributes } from 'react';
import { useTheme } from '../../theme';
import { LinkProps as ThemeLinkProps } from '../../theme/ThemeContext';
import { cn } from '../../utils';
import { isServer } from '../../utils/isServer';

export type LinkProps = React.PropsWithChildren<AnchorHTMLAttributes<HTMLAnchorElement>> & {
  variant?: 'primary' | 'secondary';
  isInverted?: boolean;
} & ThemeLinkProps;

const isExternalLink = (href: string | undefined, currentOrigin: string) => {
  if (!href || typeof href !== 'string') return false;
  const trimmed = href.trim();
  if (trimmed.startsWith('#') || trimmed.startsWith('?')) return false;
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return false;
  if (trimmed.startsWith('mailto:') || trimmed.startsWith('tel:') || trimmed.startsWith('sms:'))
    return false;
  try {
    const url = new URL(trimmed, currentOrigin);
    const current = new URL(currentOrigin);
    return url.origin !== current.origin;
  } catch {
    return false;
  }
};

const LinkComponent = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ children, className, variant = 'primary', isInverted = false, rel, ...props }, ref) => {
    // SiteOrigin allows users to provide the origin of the site for external link detection on the server when window.location is unavailable.
    const { siteOrigin } = useTheme();
    const currentOrigin = isServer() ? (siteOrigin ?? '') : window.location.origin;
    const isExternal = isExternalLink(props.href, currentOrigin);

    return (
      <UnstyledLink
        ref={ref}
        className={cn(
          'group relative inline font-medium leading-normal underline decoration-1 underline-offset-[0.3125rem] hover:decoration-2',
          variant === 'primary' && 'text-black',
          variant === 'secondary' && 'text-primary',
          isInverted && 'text-white',
          className
        )}
        rel={isExternal ? 'external' : rel}
        {...props}
      >
        {children}
        {isExternal && (
          <>
            <span className="absolute ml-1 w-6 -translate-y-0.5">
              <ExternalLink className="size-6" />
            </span>
            <span className="sr-only" data-testid="external-link-text">
              (Extern sida)
            </span>
            <span className="text-transparent" aria-hidden>
              {'\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0'}
            </span>
          </>
        )}
      </UnstyledLink>
    );
  }
);

LinkComponent.displayName = 'Link';

/**
 * A simple link component with an optional icon for external links.
 * Uses theme to determine the link component to use.
 * External links are detected automatically: internal = path starting with / (not //) or href starting with current origin.
 * On the server, pass siteOrigin to ThemeProvider for correct same-origin detection (e.g. from your app's env).
 *
 * @param {LinkProps} props - Link component props
 * @param {string} props.variant - The visual style variant ('primary' or 'secondary')
 * @param {boolean} props.isInverted - Whether the link color is inverted
 * @param {React.Ref<HTMLAnchorElement>} ref - Forwarded ref to the underlying anchor element
 */
export const Link = LinkComponent;

export type UnstyledLinkProps = Omit<LinkProps, 'variant' | 'isInverted'>;

/**
 * A link component without any of styles applied.
 * Uses the ThemeProvider to determine the link component to use.
 */
const UnstyledLink = React.forwardRef<HTMLAnchorElement, UnstyledLinkProps>(
  ({ children, className, ...props }, ref) => {
    // This allows users to override the standard a-tag with a custom link component by setting it in the ThemeProvider.
    // E.g in gatsby-pages we use Gatsbys Link component instead of the standard a-tag.
    const { LinkComponent: ThemedLinkComponent } = useTheme();

    return (
      <ThemedLinkComponent ref={ref} className={cn(className)} {...props}>
        {children}
      </ThemedLinkComponent>
    );
  }
);

UnstyledLink.displayName = 'UnstyledLink';

export { UnstyledLink };
